import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock3 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { RevenueChartCard } from "./revenue/RevenueChartCard";
import {
  SERVICES_CHART_CARD_ICON_COLOR,
  SERVICES_CONTENT_GAP,
} from "./services/constants";
import {
  DEFAULT_OFFERS_STAGE,
  OFFERS_STAGE_OPTIONS,
  parseOffersStage,
  shouldShowRoomUpgradeWidget,
} from "./offers/mockData";
import {
  downloadOffersGroupExport,
  downloadRoomUpgradeTrendExport,
  downloadRoomUpgradeUsageExport,
} from "./offers/offersTabExport";
import {
  mapRevenueTrendToChartPoints,
  mapRoomUpgradeToKpisWithComparison,
  mapRoomUpgradeUsageToSegments,
  mapSurfaceTabToSections,
} from "./offers/offersTabMappers";
import { OffersSectionWidget } from "./offers/widgets/OffersSectionWidget";
import { RoomUpgradeOfferWidget } from "./offers/widgets/RoomUpgradeOfferWidget";
import type { OffersStage } from "./offers/types";
import { SegmentPills } from "./shared/SegmentPills";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useOffersAnalytics } from "../hooks/useOffersAnalytics";

export const OffersTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  const stageOptions = useMemo(
    () =>
      OFFERS_STAGE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`analytics.offers.stages.${option.value}`, option.label),
      })),
    [t],
  );

  const stage = parseOffersStage(searchParams.get("offersStage"));
  const deferredStage = useDeferredValue(stage);
  const offersAnalytics = useOffersAnalytics(dateRange, deferredStage);
  const isContentPending =
    stage !== deferredStage ||
    offersAnalytics.isLoading ||
    offersAnalytics.isFetching ||
    offersAnalytics.isComparisonFetching;

  const surfaceData = offersAnalytics.surfaceTab.data;
  const previousRoomUpgrade = offersAnalytics.previousRoomUpgradeSummary.data;
  const metrics = useMemo(
    () =>
      mapRoomUpgradeToKpisWithComparison(
        surfaceData?.roomUpgrade,
        previousRoomUpgrade,
        offersAnalytics.previousRange !== null &&
          !offersAnalytics.isComparisonLoading &&
          !offersAnalytics.isComparisonError,
      ),
    [
      surfaceData?.roomUpgrade,
      previousRoomUpgrade,
      offersAnalytics.previousRange,
      offersAnalytics.isComparisonLoading,
      offersAnalytics.isComparisonError,
    ],
  );
  const usageSegments = mapRoomUpgradeUsageToSegments(surfaceData?.roomUpgrade);
  const revenueTrend = useMemo(
    () => mapRevenueTrendToChartPoints(offersAnalytics.revenueTrend.data),
    [offersAnalytics.revenueTrend.data],
  );
  const sections = useMemo(
    () => mapSurfaceTabToSections(surfaceData),
    [surfaceData],
  );
  const showRoomUpgrade = useMemo(
    () => shouldShowRoomUpgradeWidget(deferredStage),
    [deferredStage],
  );
  const exportableSections = useMemo(
    () => sections.filter((section) => section.surface && section.offerGroup),
    [sections],
  );

  const handleStageChange = useCallback(
    (value: OffersStage) => {
      setSearchParams((previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.set("tab", "offers");

        if (value === DEFAULT_OFFERS_STAGE) {
          nextParams.delete("offersStage");
        } else {
          nextParams.set("offersStage", value);
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const handleExportTrend = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!offersAnalytics.isReady) {
        return;
      }

      setExportingKey("trend");
      try {
        await downloadRoomUpgradeTrendExport({
          hotelId: offersAnalytics.hotelId,
          from: offersAnalytics.from,
          to: offersAnalytics.to,
          format,
        });
        toast.success(
          t(
            "analytics.offers.export.trendSuccess",
            "Room upgrade revenue trend export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.offers.export.trendError",
            "Failed to export room upgrade revenue trend",
          ),
        );
      } finally {
        setExportingKey(null);
      }
    },
    [
      offersAnalytics.from,
      offersAnalytics.hotelId,
      offersAnalytics.isReady,
      offersAnalytics.to,
      t,
    ],
  );

  const handleExportUsage = useCallback(
    (format: "csv" | "xlsx") => {
      if (!offersAnalytics.isReady || usageSegments.length === 0) {
        return;
      }

      const downloaded = downloadRoomUpgradeUsageExport({
        hotelId: offersAnalytics.hotelId,
        from: offersAnalytics.from,
        to: offersAnalytics.to,
        segments: usageSegments,
        format,
      });

      if (!downloaded) {
        toast.error(
          t(
            "analytics.offers.export.usageError",
            "Failed to export upgrade usage",
          ),
        );
        return;
      }

      toast.success(
        t(
          "analytics.offers.export.usageSuccess",
          "Upgrade usage export downloaded",
        ),
      );
    },
    [
      offersAnalytics.from,
      offersAnalytics.hotelId,
      offersAnalytics.isReady,
      offersAnalytics.to,
      usageSegments,
      t,
    ],
  );

  const handleExportGroup = useCallback(
    (sectionId: string) => async (format: "csv" | "xlsx") => {
      const section = sections.find((item) => item.id === sectionId);
      if (
        !offersAnalytics.isReady ||
        !section?.surface ||
        !section.offerGroup
      ) {
        return;
      }

      setExportingKey(sectionId);
      try {
        await downloadOffersGroupExport({
          hotelId: offersAnalytics.hotelId,
          from: offersAnalytics.from,
          to: offersAnalytics.to,
          surface: section.surface,
          offerGroup: section.offerGroup,
          format,
        });
        toast.success(
          t(
            "analytics.offers.export.groupSuccess",
            "Offer group export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.offers.export.groupError",
            "Failed to export offer group",
          ),
        );
      } finally {
        setExportingKey(null);
      }
    },
    [
      offersAnalytics.from,
      offersAnalytics.hotelId,
      offersAnalytics.isReady,
      offersAnalytics.to,
      sections,
      t,
    ],
  );

  const canExport =
    offersAnalytics.isReady &&
    !offersAnalytics.isLoading &&
    !offersAnalytics.isError &&
    exportingKey === null;

  const handleExportTab = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      if (showRoomUpgrade) {
        await handleExportTrend(format);
      }

      for (const section of exportableSections) {
        await handleExportGroup(section.id)(format);
      }
    },
    [
      canExport,
      exportableSections,
      handleExportGroup,
      handleExportTrend,
      showRoomUpgrade,
    ],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport,
      onExport: handleExportTab,
    }),
    [canExport, handleExportTab],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  const detailsTitle = t("analytics.offers.detailsTitle", "Offers details");
  const detailsCardProps = {
    title: detailsTitle,
    icon: <Clock3 />,
    iconColor: SERVICES_CHART_CARD_ICON_COLOR,
    width: "full" as const,
    showExport: false,
  };

  let mainContent: React.ReactNode;

  if (!offersAnalytics.isReady) {
    mainContent = (
      <RevenueChartCard {...detailsCardProps}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.offers.missingFilters",
            "Select a hotel and date range to view offers analytics.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else if (offersAnalytics.isError) {
    mainContent = (
      <RevenueChartCard {...detailsCardProps}>
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.offers.loadError",
            "Unable to load offers analytics. Try again.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else {
    const showEmptyStage = sections.length === 0 && !showRoomUpgrade;

    mainContent = (
      <>
        {showRoomUpgrade ? (
          <RoomUpgradeOfferWidget
            metrics={metrics}
            usageSegments={usageSegments}
            revenueTrend={revenueTrend}
            onExportTrend={handleExportTrend}
            exportTrendDisabled={exportingKey === "trend"}
            onExportUsage={handleExportUsage}
            exportUsageDisabled={usageSegments.length === 0}
          />
        ) : null}

        {sections.map((section) => (
          <OffersSectionWidget
            key={section.id}
            section={section}
            onExport={
              section.surface && section.offerGroup
                ? handleExportGroup(section.id)
                : undefined
            }
            exportDisabled={exportingKey === section.id}
          />
        ))}

        {showEmptyStage ? (
          <RevenueChartCard {...detailsCardProps}>
            <p className="text-sm text-[#737373]">
              {t(
                "analytics.offers.emptyStage",
                "No offer breakdown available yet for this stage.",
              )}
            </p>
          </RevenueChartCard>
        ) : null}
      </>
    );
  }

  return (
    <section className="space-y-6 font-sans">
      <div
        className={cn(
          "flex w-full max-w-full flex-col transition-opacity duration-150",
          isContentPending && "opacity-70",
        )}
        style={{ gap: SERVICES_CONTENT_GAP }}
      >
        <SegmentPills
          value={stage}
          options={stageOptions}
          onChange={handleStageChange}
          ariaLabel={t("analytics.aria.offersStages", "Offers stages")}
        />

        {mainContent}
      </div>
    </section>
  );
};
